import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagStatus } from '@factorialco/f0-react'
import { ArrowRight, BarGraph, Download, LockLocked } from '@factorialco/f0-react/icons/app'
import {
  CompensationsPayrollRunDocument,
  CompletePayrollRunDocument,
  CycleRouteMetaDocument,
  PayrollRunInfoPayrollRunFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'

import app from 'modules/core/store/models/app'

import {
  PayrollRunStatus,
  getPayrollRunStatusText,
  getRawStatusConfig,
} from 'modules/compensations/lib/payrollRunStatus'
import {
  getDaysSinceCycleStart,
  trackCycleStatusChanged,
} from 'modules/compensations/lib/cycleStatusTracking'

type PayrollRunInfoProps = PayrollRunInfoPayrollRunFragment

interface CompleteCycleModalProps {
  payrollRun: PayrollRunInfoProps
  companyId: number
  country: string
  onConfirm: () => void
  onClose: () => void
}

const CompleteCycleModal = ({
  payrollRun,
  companyId,
  country,
  onConfirm,
  onClose,
}: CompleteCycleModalProps) => {
  const [completePayrollRun] = useMutation(CompletePayrollRunDocument, {
    extractorFn: (result) => result.compensationsMutations.completeCompensationsPayrollRun,
  })

  // FIXME: Once i18nDateToString is ressurected move the format to the i18n translations
  const formatedStartsOn = stringToDate(payrollRun.startsOn, 'backend_datetime').format('MMMM YYYY')

  const title = i18n.t('compensations.PayrollRunCompensationsList.complete_modal_cycle_title', {
    period: formatedStartsOn,
  })

  const currentStatus = payrollRun.status || PayrollRunStatus.IN_REVIEW || PayrollRunStatus.OPEN
  const nextStatus = PayrollRunStatus.COMPLETED

  const currentStatusText = getPayrollRunStatusText(currentStatus)
  const nextStatusText = getPayrollRunStatusText(nextStatus)
  const currentStatusConfig = getRawStatusConfig(currentStatus)

  const handleConfirm = async () => {
    const result = await completePayrollRun({
      variables: {
        payrollRunId: payrollRun.id,
      },
      refetchQueries: [CompensationsPayrollRunDocument, CycleRouteMetaDocument],
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success && handled.data.payrollRun) {
      trackCycleStatusChanged({
        cycle_id: String(payrollRun.id),
        company_id: String(companyId),
        previous_status: currentStatus,
        new_status: nextStatus,
        triggered_by_action: 'complete',
        cycle_period_start: payrollRun.startsOn,
        cycle_period_end: payrollRun.endsOn,
        days_since_cycle_creation: getDaysSinceCycleStart(payrollRun.startsOn),
        country,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onConfirm()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    return handled
  }

  return (
    <Modal
      illustration="alert"
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            {i18n.t('common.confirm')}
          </Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s24">
        <Box alignItems="center" flexDirection="row" gap="s12">
          <F0TagStatus text={currentStatusText} variant={currentStatusConfig.variant} />
          <F0Icon icon={ArrowRight} />
          <F0TagStatus text={nextStatusText} variant="positive" />
        </Box>

        <Box flexDirection="column" gap="s12">
          <Text.Normal size="300" color="textSecondary">
            {i18n.t('compensations.PayrollRunCompensationsList.complete_modal_consequences_title')}
          </Text.Normal>

          <Box flexDirection="column" gap="s8">
            <Box alignItems="flexStart" flexDirection="row" gap="s8">
              <Box paddingTop="s2">
                <F0Icon icon={LockLocked} />
              </Box>
              <Text.Normal size="300" color="textPrimary">
                {i18n.t('compensations.PayrollRunCompensationsList.complete_modal_edits_prevented')}
              </Text.Normal>
            </Box>

            <Box alignItems="flexStart" flexDirection="row" gap="s8">
              <Box paddingTop="s2">
                <F0Icon icon={Download} />
              </Box>
              <Text.Normal size="300" color="textPrimary">
                {i18n.t(
                  'compensations.PayrollRunCompensationsList.complete_modal_payroll_results_available'
                )}
              </Text.Normal>
            </Box>

            <Box alignItems="flexStart" flexDirection="row" gap="s8">
              <Box paddingTop="s2">
                <F0Icon icon={BarGraph} />
              </Box>
              <Text.Normal size="300" color="textPrimary">
                {i18n.t(
                  'compensations.PayrollRunCompensationsList.complete_modal_financial_workspace_available'
                )}
              </Text.Normal>
            </Box>
          </Box>
        </Box>

        <>
          <Link.Href
            href={helpCenterUrl('compensations_cycle_statuses')}
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            {i18n.t('compensations.PayrollRunCompensationsList.learn_more_cycle_statuses')}
          </Link.Href>
        </>
      </Box>
    </Modal>
  )
}

export { CompleteCycleModal }
